import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import compose from 'lodash.flowright';
import { graphql } from 'react-apollo';
import pluralize from 'pluralize';

import { openAlert as openAlertAction } from '../../../actions/modal-actions';
import { PhotoDownloadCreate } from '../../../graphql/mutations/photo-downloads';
import FormGroup from '../FormGroup';
import Button from '../Button';
import Alert from '../Alert';
import { getErrorString } from '../../../util/normalize-errors';
import { MeQuery } from '../../../graphql/queries/account';
import usePhotoDownload from '../../../hooks/usePhotoDownload';
import { IMAGE_SIZE_CODE } from '../../../constants/image-size-codes';
import useBasename from '../../routing/useBasename';
import CustomControl from '../CustomControl';
import InfoCircleTip from '../InfoCircleTip';

const propTypes = {
  totalPhotos: PropTypes.number.isRequired,
  photoDownloadCreateInput: PropTypes.shape({
    photo_ids: PropTypes.shape({
      select_all: PropTypes.bool,
      selected_ids: PropTypes.array,
      ignored_ids: PropTypes.array,
    }),
  }),
  photoDownloadCreate: PropTypes.func.isRequired,
  openAlert: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    email_address: PropTypes.string,
  }),
  name: PropTypes.string,
  downloadOriginalPhoto: PropTypes.bool,
  photoForegroundsOnly: PropTypes.bool,
  allowPhotoTagsCsv: PropTypes.bool,
};

const PhotoDownload = ({
  totalPhotos,
  photoDownloadCreateInput,
  photoDownloadCreate,
  openAlert,
  onClose,
  user,
  name: defaultName,
  downloadOriginalPhoto,
  photoForegroundsOnly,
  allowPhotoTagsCsv,
}) => {
  const [name, setName] = useState(defaultName || '');
  const [email, setEmail] = useState(user?.email_address ?? '');
  const [includePhotoTagsCsv, setIncludePhotoTagsCsv] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [photoId] = photoDownloadCreateInput?.photo_ids?.selected_ids ?? [];

  const basename = useBasename();
  const downloadsUrl = `/app/${basename}/account/settings/downloads`;

  const { handleDownload } = usePhotoDownload({
    photoId,
    imageSizeCode: IMAGE_SIZE_CODE.LARGE2X,
    onDownload: () => {
      onClose?.();
    },
  });

  const isSinglePhotoDownload =
    photoDownloadCreateInput?.photo_ids?.selected_ids.length === 1 &&
    // If it's select-all, we don't know the single photo ID, even if it's really
    // a single photo.
    !photoDownloadCreateInput?.photo_ids?.select_all;

  useEffect(() => {
    if (isSinglePhotoDownload) {
      handleDownload({
        downloadOriginal: downloadOriginalPhoto,
        downloadPhotoForeground: photoForegroundsOnly,
      });
      onClose?.();
    }
  }, [isSinglePhotoDownload]);

  const isFormValid = () => {
    if (!name) {
      return false;
    }

    return true;
  };

  const onSubmit = async event => {
    event.preventDefault();

    if (!isFormValid()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      await photoDownloadCreate({
        name,
        email_address: email || undefined,
        download_original_photo: downloadOriginalPhoto,
        photo_foregrounds_only: photoForegroundsOnly,
        include_photo_tags_csv: allowPhotoTagsCsv
          ? includePhotoTagsCsv
          : undefined,
        ...photoDownloadCreateInput,
      });

      setLoading(false);
      openAlert({
        type: 'success',
        title: 'Download is being processed!',
        message: (
          <>
            <p>
              Your download is being prepared and you&rsquo;ll be notified when
              it&rsquo;s ready. You can find its status in the downloads section
              under{' '}
              <a target="_blank" href={downloadsUrl} rel="noreferrer">
                Account Settings &rarr; Downloads.
              </a>
            </p>
            <p>
              <span className="text-bold text-dark">Note:</span> Export files
              will be available for the next seven days
            </p>
          </>
        ),
      });
      onClose?.();
    } catch (err) {
      setError(getErrorString(err));
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <p>
        You are about to create a new download for a total of {totalPhotos}{' '}
        {pluralize('photo', totalPhotos)}.
      </p>

      <form onSubmit={onSubmit}>
        <FormGroup
          id="name"
          type="text"
          label="Name Your Download"
          name="name"
          value={name}
          onChange={event => setName(event.target.value)}
          required
        />

        <FormGroup
          id="email"
          type="email"
          label="Email Address (optional)"
          name="email"
          value={email}
          labelTip="If empty, we will send the email to the account's email address."
          onChange={event => setEmail(event.target.value)}
          className={allowPhotoTagsCsv ? 'mb-2' : undefined}
        />

        {allowPhotoTagsCsv && (
          <div className="mb-2">
            <CustomControl
              type="checkbox"
              id="include-photo-tags-csv"
              label={
                <Fragment>
                  Include a photo tags CSV
                  <InfoCircleTip
                    id="include-photo-tags-csv-tooltip"
                    className="ml-1"
                    tip={
                      <Fragment>
                        Adds a CSV file to the zip listing
                        <br />
                        the tags applied to each photo.
                      </Fragment>
                    }
                  />
                </Fragment>
              }
              checked={includePhotoTagsCsv}
              onChange={() => setIncludePhotoTagsCsv(prev => !prev)}
            />
          </div>
        )}

        {error && <Alert type="danger">{error}</Alert>}
        <Button
          type="submit"
          btnStyle="primary"
          className="mt-1"
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Loading...' : 'Download Photos'}
        </Button>
      </form>
    </Fragment>
  );
};

PhotoDownload.propTypes = propTypes;

export { PhotoDownload as PhotoDownloadComponent };

const mapDispatchToProps = {
  openAlert: openAlertAction,
};

const withPhotoDownloadCreate = graphql(PhotoDownloadCreate, {
  props: ({ mutate }) => ({
    photoDownloadCreate: ({
      name,
      email_address: emailAddress,
      ...input // Each modal user is responsible for filling up this info.
    }) =>
      mutate({
        variables: {
          input: {
            name,
            email_address: emailAddress,
            ...input,
          },
        },
      }),
  }),
});

const withUserData = graphql(MeQuery, {
  props: ({ data } = {}) => ({
    user: data?.me,
  }),
});

export default compose(
  withPhotoDownloadCreate,
  withUserData,
  connect(null, mapDispatchToProps),
)(PhotoDownload);
